/**
 * Spec 218 PR1 — bidirectional math for an invoice line on the web.
 *
 * Mirrors `mobile/src/screens/invoiceLineMath.ts` byte-for-byte (the
 * AEAT rounding behaviour must be identical cross-stack so a draft
 * built on web emits to the same totals on mobile and vice-versa).
 * Keep both files in sync — the duplication is intentional: the
 * web bundle should not pull anything from `mobile/src/screens/*.tsx`
 * (those import React Native components).
 *
 * The user can edit any of base / vatRate / total. The last edited
 * field is the "canonical" one — the value that must be preserved
 * when vatRate changes or when one of the other two is recomputed.
 * The invariant `base + vatAmount = total` holds within ε=0.01 by
 * construction, because vatAmount is derived as `total - base`
 * after both have been rounded.
 *
 * Pure function (no React, no DOM); easily unit-testable. The
 * EmitInvoice screen wraps it in setState.
 */

export type InvoiceLineCanonical = 'base' | 'total';

export interface InvoiceLineMathInput {
  base: number;
  vatRate: number;
  total: number;
  canonical: InvoiceLineCanonical;
}

export interface InvoiceLineMathResult {
  base: number;
  vatRate: number;
  total: number;
  vatAmount: number;
}

const round2 = (n: number): number => Math.round(n * 100) / 100;

export function recomputeInvoiceLine(input: InvoiceLineMathInput): InvoiceLineMathResult {
  const { vatRate, canonical } = input;
  if (canonical === 'base') {
    const base = round2(input.base);
    const total = round2(base * (1 + vatRate / 100));
    const vatAmount = round2(total - base);
    return { base, vatRate, total, vatAmount };
  }
  const total = round2(input.total);
  const base = round2(total / (1 + vatRate / 100));
  const vatAmount = round2(total - base);
  return { base, vatRate, total, vatAmount };
}

/**
 * Sum the totals across an array of invoice lines. Each line's
 * total/vatAmount/base get re-rounded to 2 decimals before summing
 * so a stale `vatAmount` field doesn't drift the totals. The result
 * is the trio Verifacti expects on the wire (totalBase / totalVat /
 * total) plus a per-rate breakdown that the PDF renderer can use.
 */
export interface InvoiceTotals {
  totalBase: number;
  totalVat: number;
  total: number;
  /** vatRate → { base, vat, total }. Useful for the PDF's
   *  "desglose por tipo IVA" table. */
  byRate: Record<string, { base: number; vat: number; total: number }>;
}

export function computeInvoiceTotals(
  lines: ReadonlyArray<{ base: number; vatRate: number; total: number; vatAmount: number }>,
): InvoiceTotals {
  let totalBase = 0;
  let totalVat = 0;
  let total = 0;
  const byRate: Record<string, { base: number; vat: number; total: number }> = {};
  for (const l of lines) {
    const base = round2(l.base);
    const vat = round2(l.vatAmount);
    const lineTotal = round2(l.total);
    totalBase = round2(totalBase + base);
    totalVat = round2(totalVat + vat);
    total = round2(total + lineTotal);
    const rateKey = String(l.vatRate);
    const bucket = byRate[rateKey] ?? { base: 0, vat: 0, total: 0 };
    bucket.base = round2(bucket.base + base);
    bucket.vat = round2(bucket.vat + vat);
    bucket.total = round2(bucket.total + lineTotal);
    byRate[rateKey] = bucket;
  }
  return { totalBase, totalVat, total, byRate };
}
